import {
  TextField,
  Box,
  Grid,
  Button,
  Card,
  InputLabel,
  Collapse,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Alert,
  AlertTitle,
  Chip,
  Divider,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/Email';

const ScheduleReport = ({
  open,
  schedule,
  onScheduleChange,
  onSubmit
}) => {
  const frequencyOptions = [
    { id: 'daily', name: "Every Day" },
    { id: 'weekly', name: "Every 1 Week" },
    { id: 'fortnightly', name: "Every 15 Days" },
    { id: 'monthly', name: "Every 30 Days" }
  ];

  const safeSchedule = {
    frequency: schedule.frequency || 'daily',
    time: schedule.time || "09:00",
    recipients: schedule.recipients || ""  // Changed from emails to recipients
  };

  // Format time for display (e.g., "09:00" becomes "9:00 AM")
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Validate comma-separated email addresses
  const validateEmails = (emailString) => {
    if (!emailString) return true; // Empty is valid (optional field)
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = emailString.split(',').map(email => email.trim());
    
    return emails.every(email => {
      if (email === '') return true; // Allow empty entries between commas
      return emailRegex.test(email);
    });
  };

  // Fixed: Using recipients instead of emails
  const isEmailValid = validateEmails(safeSchedule.recipients);

  return (
    <Grid item xs={12}>
      <Collapse in={open}>
        <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box p={4}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={1}>
                  <ScheduleIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={500}>
                    Schedule Report Email
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Configure when and how often you want to receive this report via email.
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <InputLabel sx={{ mb: 1, fontWeight: 500 }}>Frequency</InputLabel>
                <FormControl fullWidth>
                  <Select
                    value={safeSchedule.frequency}
                    onChange={(e) => onScheduleChange("frequency", e.target.value)}
                    displayEmpty
                    size="small"
                    sx={{ borderRadius: 1 }}
                  >
                    {frequencyOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <InputLabel sx={{ mb: 1, fontWeight: 500 }}>Time</InputLabel>
                <TextField
                  variant="outlined"
                  type="time"
                  value={safeSchedule.time}
                  onChange={(e) => onScheduleChange("time", e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300, // 5 min
                  }}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>

              {/* Email Field */}
              <Grid item xs={12}>
                <InputLabel sx={{ mb: 1, fontWeight: 500 }}>
                  Recipient Emails
                </InputLabel>
                <TextField
                  variant="outlined"
                  placeholder="email1@example.com, email2@example.com"
                  value={safeSchedule.recipients}  // Changed from emails to recipients
                  onChange={(e) => onScheduleChange("recipients", e.target.value)}  // Changed to recipients
                  fullWidth
                  size="small"
                  error={!isEmailValid && safeSchedule.recipients !== ''}  // Changed to recipients
                  helperText={
                    !isEmailValid && safeSchedule.recipients !== ''  // Changed to recipients
                      ? "Please enter valid email addresses separated by commas"
                      : "Enter multiple email addresses separated by commas"
                  }
                  InputProps={{
                    startAdornment: (
                      <EmailIcon 
                        sx={{ 
                          mr: 1, 
                          color: isEmailValid ? 'text.secondary' : 'error.main' 
                        }} 
                      />
                    ),
                  }}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>

              {/* Informational Alert */}
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  icon={<InfoIcon fontSize="small" />}
                  sx={{
                    borderRadius: 1,
                    '& .MuiAlert-message': { width: '100%' }
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 500 }}>Scheduling Recommendation</AlertTitle>
                  <Typography variant="body2">
                    To avoid conflicts with other scheduled reports, please select a different delivery time. This ensures efficient email distribution and timely delivery of all reports.
                  </Typography>
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={1}
                >
                  <Box display="flex" alignItems="center">
                    <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {safeSchedule.time ? `Delivers at ${formatTime(safeSchedule.time)}` : 'Select your preferred delivery time'}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                    endIcon={<ArrowForwardIcon fontSize="small" />}
                    disabled={!isEmailValid}
                    sx={{
                      borderRadius: 1.5,
                      px: 3,
                      py: 0.8,
                      fontWeight: 500,
                      minWidth: 160
                    }}
                  >
                    Schedule Report
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Collapse>
    </Grid>
  )
};

export default ScheduleReport;